import { LogLevel, OpenIdConfiguration, StsConfigStaticLoader } from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';
import { AppConfigService } from '../services/app-config.service';
import { ACTIVE_IDP_KEY } from './models/auth-storage-keys';
import { IdpConfigId } from './models/idp-config-id.enum';

/**
 * Factory that assembles OIDC configurations for PingID (configId: 'oidc') and Okta
 * (configId: 'okta') from `AppConfigService`.
 *
 * PingID is only registered when `oidc.client_id` is non-empty. When it is empty
 * (e.g. UAT/PROD environments where the application has not been registered in the
 * CIAM admin panel), only the Okta config is registered to avoid lib validation
 * errors and to skip directly to the Okta flow.
 *
 * Authentication flow (orchestrated by LoginRelayComponent + AppComponent.handleAuthFallback):
 *  1. Try PingID first (if registered) → if IdP returns ?error → fallback to Okta
 *  2. Try Okta                         → if IdP returns ?error → navigate to /login-error
 *
 * `unauthorizedRoute` is intentionally omitted — the library must not navigate away
 * before AppComponent.handleAuthFallback() has a chance to read the LoginResponse[].
 */
export function authConfigFactory(appConfig: AppConfigService): StsConfigStaticLoader {
  appConfig.loadConfig();

  const oidc = appConfig.config.oidc;
  const okta = appConfig.config.okta;

  const sharedConfig: Partial<OpenIdConfiguration> = {
    scope: 'openid profile email offline_access',
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    ignoreNonceAfterRefresh: true,
    renewTimeBeforeTokenExpiresInSeconds: 90,
    triggerRefreshWhenIdTokenExpired: false,
    startCheckSession: false,
    autoUserInfo: false,
    postLoginRoute: '/main',
    logLevel: environment.production ? LogLevel.Error : LogLevel.Debug,
    secureRoutes: [],
  };

  const oidcConfig: OpenIdConfiguration = {
    ...sharedConfig,
    scope: 'openid profile email user offline_access',
    authority: oidc?.issuer,
    clientId: oidc?.client_id,
    redirectUrl: oidc?.redirect_uri,
    postLogoutRedirectUri: oidc?.post_logout_redirect_uri,
    configId: IdpConfigId.Oidc,
  };

  const oktaConfig: OpenIdConfiguration = {
    ...sharedConfig,
    authority: okta?.issuer,
    clientId: okta?.client_id,
    redirectUrl: okta?.redirect_uri,
    postLogoutRedirectUri: okta?.post_logout_redirect_uri,
    configId: IdpConfigId.Okta,
  };

  const configs = oidc?.client_id ? [oidcConfig, oktaConfig] : [oktaConfig];

  const isInAuthFlow = typeof location !== 'undefined' && location.pathname.startsWith('/sso/');
  const activeId = sessionStorage.getItem(ACTIVE_IDP_KEY);
  let filtered = configs;
  if (!isInAuthFlow && (activeId === IdpConfigId.Okta || activeId === IdpConfigId.Oidc)) {
    const narrowed = configs.filter((c) => c.configId === activeId);
    if (narrowed.length) {
      filtered = narrowed;
    }
  }

  return new StsConfigStaticLoader(filtered);
}
